import {Component, DestroyRef, inject, Input, OnInit} from '@angular/core';
import {AreaChartModule} from '@swimlane/ngx-charts';
import {curveStep} from 'd3-shape';
import {map, Observable} from 'rxjs';
import {ApiWeatherForecastHourResponseDto} from '@birco/rainmesh-api';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {LocalizedNumberPipe} from '../../../../shared/pipes/localized-number.pipe';
import {LocaleService} from '../../../../translation/services/locale.service';

type PrecipitationChartPoint = {
  name: Date;
  value: number;
  precipitationAmount: number;
};

@Component({
  selector: 'app-chart-precipitation',
  imports: [
    AreaChartModule,
    TranslatePipe,
    LocalizedNumberPipe,
  ],
  templateUrl: './chart-precipitation.component.html',
  styleUrl: './chart-precipitation.component.scss'
})
export class ChartPrecipitationComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly localeService = inject(LocaleService);
  private static readonly timeSuffixKey = 'default.timeSuffix';

  @Input() hourlyForecast$?: Observable<ApiWeatherForecastHourResponseDto[]>
  chartData: { name: string, series: PrecipitationChartPoint[] }[] = []

  curve = curveStep;
  customColors = [
    {name: 'Niederschlag', value: '#A8D0FF'},
    {name: 'Niederschlagsmenge', value: '#0F5FBF'}
  ];

  ngOnInit() {
    this.hourlyForecast$?.pipe(
      map((weatherArray) => [{
        name: 'Niederschlag',
        series: weatherArray
          .map(w => ({
            name: new Date(w.dateTime),
            value: Math.round(w.precipitationChance * 100),
            precipitationAmount: w.precipitationAmount,
          }))
      }]),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(data => {
      this.chartData = [...data];
    });
  }

  get min() {
    return 0;
  }

  get max() {
    const values = this.chartData[0] ? this.chartData[0].series.map(s => s.value) : [0];
    const maxValue = Math.max(...values);
    return maxValue < 95 ? maxValue + 5 : 100;
  }

  formatTemp(value: number) {
    return `${Math.round(value)}%`
  }

  formatTime = (value: Date) => `${value.toLocaleTimeString(this.localeService.userLanguage, {hour: '2-digit', minute: '2-digit'})}${this.getTimeSuffix()}`

  getTooltipPoint(model: PrecipitationChartPoint | PrecipitationChartPoint[] | undefined): PrecipitationChartPoint | null {
    if (Array.isArray(model)) {
      return model[0] ?? null;
    }

    return model ?? null;
  }

  formatTooltipDate(point: PrecipitationChartPoint): string {
    return this.formatTime(point.name);
  }

  private getTimeSuffix(): string {
    const suffix = this.translateService.instant(ChartPrecipitationComponent.timeSuffixKey);
    return suffix ? ` ${suffix}` : '';
  }
}
